<template>
  <div>
    <DxForm id="formularioMarcoComisiones" ref="formInstance" :form-data="model" label-location="top"
      :show-colon-after-label="false" :visible="model != null" validation-group="validacionMarcoComisiones">
      <!-- xs: 768, sm: 992, md:1200 -->
      <DxColCountByScreen :xs="6" :sm="6" :md="6" :lg="6" />

      <!-- Datos del marco de comisión -->
      <DxGroupItem :col-span="6" :col-count="6">
        <DxSimpleItem data-field="Denominacion" editor-type="dxTextBox" :col-span="6">
          <DxLabel :text="$t('marcosComisiones.denominacion')" />
          <DxRequiredRule :message="$t('marcosComisiones.validacion.denominacion_requerida')" />
        </DxSimpleItem>
      </DxGroupItem>

    </DxForm>
  </div>
</template>

<script setup lang="ts">
import { messageBoxResult } from '@components/dialogs/message-box';
import { type CreateMarcoComisionesModel, type EditMarcoComisionesModel } from '@api/api';
import { useModalBuilder, type ToolbarReference } from '@helpers/modal-building-helper';
import { type DxButton } from 'devextreme-vue';
import {
  DxForm,
  DxLabel,
  DxColCountByScreen,
  DxRequiredRule,
  DxGroupItem,
  DxSimpleItem
} from "devextreme-vue/form";
import { getCurrentInstance, onMounted, ref } from 'vue';
import { useIntl } from '@services/i18n';
import validationEngine from "devextreme/ui/validation_engine";
import { useApi } from '@api/integration';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import { ApiError } from '@/.shared/services/http';
import { MARCO_COMISIONES_WITH_DUPLICATED_DENOMINACION } from '@/.shared/errors/codes';


const api = useApi();
const formInstance = ref<DxForm>();
const intl = useIntl();
const props = defineProps({
  model: {
    type: Object as () => EditMarcoComisionesModel | CreateMarcoComisionesModel,
    required: false
  },
  isEditMode: {
    type: Boolean,
    required: true
  }
});

onMounted(async () => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!)

  configureButtons(modal);
});

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());
  okButton.update(w => w.text = `${intl.i18n.t("shared.ui.buttons.save")}`);
  okButton.update(w => w.onClick = async () => {
    saveOrEdit().then((value) => {
      if (value)
        modal.close(messageBoxResult.ok)
    });
  }
  );
  cancelButton.update(w => w.onClick = () => modal.close(messageBoxResult.cancel));
}

const saveOrEdit = async () => {
  var res = validationEngine.validateGroup("validacionMarcoComisiones");
  if (res.isValid) {
    if (props.isEditMode) {
      try {
        await api.callStandardApi("editMarcoComisiones", { showLoading: true }, props.model as EditMarcoComisionesModel)
          .then(() => toastSuccess(intl.i18n.t('marcosComisiones.edicion_exito')));
      } catch (e: any) {
        if (e instanceof ApiError && e.details?.errorList.length === 1 && [MARCO_COMISIONES_WITH_DUPLICATED_DENOMINACION].includes(e.details.errorList[0].Code)) {
          toastError(intl.i18n.t(`shared.errors.api.${e.details.errorList[0].Code}`))
        }
        else throw e;
        return false;
      }
    } else {
      try {
        await api.callStandardApi("createMarcoComisiones", { showLoading: true }, props.model as CreateMarcoComisionesModel)
          .then(() => toastSuccess(intl.i18n.t('marcosComisiones.creacion_exito')))
      } catch (e: any) {
        if (e instanceof ApiError && e.details?.errorList.length === 1 && [MARCO_COMISIONES_WITH_DUPLICATED_DENOMINACION].includes(e.details.errorList[0].Code)) {
          toastError(intl.i18n.t(`shared.errors.api.${e.details.errorList[0].Code}`))
        }
        else throw e;
        return false;
      }
    }
    return true;
  } else {
    toastError(intl.i18n.t('marcosComisiones.revise_validacion'));
  }

  return false;
}

</script>